/**
 * Song Marketplace Page
 * Browse, preview, and purchase songs (WAV/MP3)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import { useAuth } from '../context/AuthContext';
import NotificationCenter from '../components/NotificationCenter';
import {
  Music,
  Play,
  Pause,
  Search,
  Filter,
  ShoppingCart,
  Download,
  Star,
  Clock,
  Heart,
  Share2,
  ChevronDown,
  Grid,
  List,
  TrendingUp,
  Sparkles,
  DollarSign,
  Headphones,
  Upload,
  BarChart3,
  X,
  Sliders,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Genre options
const GENRES = [
  'All Genres', 'Hip Hop', 'R&B', 'Pop', 'Rock', 'Electronic', 'EDM',
  'Trap', 'Lo-Fi', 'Jazz', 'Classical', 'Country', 'Reggae', 'Latin'
];

// License types with descriptions
const LICENSE_TYPES = {
  lease: { name: 'Lease', description: 'Non-exclusive license for personal/small commercial use' },
  standard: { name: 'Standard', description: 'Non-exclusive license with more distribution rights' },
  exclusive: { name: 'Exclusive', description: 'Full ownership rights, song removed from marketplace' },
};

// Song Card Component
const SongCard = ({ song, onPlay, onBuy, isPlaying, currentSongId }) => {
  const navigate = useNavigate();
  const isCurrentlyPlaying = isPlaying && currentSongId === song.id;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden hover:border-zinc-700 transition-all group"
    >
      {/* Cover Art */}
      <div 
        className="relative aspect-square cursor-pointer"
        onClick={() => navigate(`/songs/${song.id}`)}
      >
        {song.cover_art_url ? (
          <img 
            src={song.cover_art_url} 
            alt={song.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-violet-600 to-purple-800 flex items-center justify-center">
            <Music className="w-12 h-12 text-white/50" />
          </div>
        )}
        
        {/* Play Button Overlay */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <button
            onClick={(e) => { e.stopPropagation(); onPlay(song); }}
            className="w-14 h-14 rounded-full bg-violet-500 hover:bg-violet-400 flex items-center justify-center transition-colors"
          >
            {isCurrentlyPlaying ? (
              <Pause className="w-6 h-6 text-white" />
            ) : (
              <Play className="w-6 h-6 text-white ml-1" />
            )}
          </button>
        </div>
        
        {/* Featured Badge */}
        {song.is_featured && (
          <div className="absolute top-2 left-2 px-2 py-1 bg-amber-500/90 rounded-full flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-white" />
            <span className="text-xs text-white font-medium">Featured</span>
          </div>
        )}
        
        {/* Price Badge */}
        <div className="absolute bottom-2 right-2 px-3 py-1 bg-black/70 rounded-full">
          <span className="text-sm font-bold text-white">
            {song.price === 0 ? 'FREE' : `$${song.price.toFixed(2)}`}
          </span>
        </div>
      </div>
      
      {/* Info */}
      <div className="p-4">
        <h3 
          className="font-semibold text-white truncate cursor-pointer hover:text-violet-400"
          onClick={() => navigate(`/songs/${song.id}`)}
        >
          {song.title}
        </h3>
        <p className="text-sm text-zinc-400 truncate">{song.artist_name}</p>
        
        <div className="flex items-center gap-3 mt-3 text-xs text-zinc-500">
          {song.bpm && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {song.bpm} BPM
            </span>
          )}
          {song.key && (
            <span>{song.key}</span>
          )}
          <span className="flex items-center gap-1">
            <Play className="w-3 h-3" />
            {song.play_count || 0}
          </span>
        </div>
        
        {/* Rating */}
        {song.rating_count > 0 && (
          <div className="flex items-center gap-1 mt-2">
            <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
            <span className="text-sm text-white">{song.average_rating.toFixed(1)}</span>
            <span className="text-xs text-zinc-500">({song.rating_count})</span>
          </div>
        )}
        
        {/* Quick Buy Button */}
        <Button
          onClick={() => onBuy(song)}
          className="w-full mt-4 bg-violet-600 hover:bg-violet-500"
          size="sm"
        >
          <ShoppingCart className="w-4 h-4 mr-2" />
          {song.price === 0 ? 'Get Free' : 'Buy Now'}
        </Button>
      </div>
    </motion.div>
  );
};

// Audio Player Component
const AudioPlayer = ({ song, isPlaying, onTogglePlay, onClose }) => {
  const audioRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  
  useEffect(() => {
    if (!song || !audioRef.current) return;
    
    const audio = audioRef.current;
    
    const handleTimeUpdate = () => {
      setProgress((audio.currentTime / audio.duration) * 100);
    };
    
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };
    
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [song]);
  
  useEffect(() => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.play();
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying]);
  
  if (!song) return null;
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      className="fixed bottom-0 left-0 right-0 bg-zinc-900/95 backdrop-blur border-t border-zinc-800 px-4 py-3 z-50"
    >
      <audio
        ref={audioRef}
        src={song.preview_url}
        onEnded={() => onTogglePlay()}
      />
      
      <div className="max-w-7xl mx-auto flex items-center gap-4">
        {/* Song Info */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-12 h-12 rounded bg-violet-500/20 flex items-center justify-center flex-shrink-0">
            <Music className="w-6 h-6 text-violet-400" />
          </div>
          <div className="min-w-0">
            <p className="text-white font-medium truncate">{song.title}</p>
            <p className="text-sm text-zinc-400 truncate">{song.artist_name}</p>
          </div>
        </div>
        
        {/* Controls */}
        <div className="flex items-center gap-4">
          <button
            onClick={onTogglePlay}
            className="w-10 h-10 rounded-full bg-violet-500 hover:bg-violet-400 flex items-center justify-center"
          >
            {isPlaying ? (
              <Pause className="w-5 h-5 text-white" />
            ) : (
              <Play className="w-5 h-5 text-white ml-0.5" />
            )}
          </button>
        </div>
        
        {/* Progress */}
        <div className="flex-1 max-w-md hidden sm:flex items-center gap-2">
          <span className="text-xs text-zinc-500 w-10">{formatTime((progress / 100) * duration)}</span>
          <div className="flex-1 h-1 bg-zinc-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-violet-500 transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-xs text-zinc-500 w-10">{formatTime(duration)}</span>
        </div>
        
        {/* Close */}
        <button
          onClick={onClose}
          className="text-zinc-400 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </motion.div>
  );
};

// Main Page Component
export default function SongMarketplacePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [songs, setSongs] = useState([]);
  const [featuredSongs, setFeaturedSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedGenre, setSelectedGenre] = useState(searchParams.get('genre') || 'All Genres');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'newest');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Player state
  const [currentSong, setCurrentSong] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Filters
  const [priceRange, setPriceRange] = useState([0, 100]);
  const [bpmRange, setBpmRange] = useState([60, 180]);
  
  // Fetch songs
  const fetchSongs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        sort_by: sortBy,
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (selectedGenre && selectedGenre !== 'All Genres') params.append('genre', selectedGenre);
      if (priceRange[0] > 0) params.append('min_price', priceRange[0].toString());
      if (priceRange[1] < 100) params.append('max_price', priceRange[1].toString());
      if (bpmRange[0] > 60) params.append('bpm_min', bpmRange[0].toString());
      if (bpmRange[1] < 180) params.append('bpm_max', bpmRange[1].toString());
      
      const response = await fetch(`${API_URL}/api/songs/browse?${params}`);
      if (response.ok) {
        const data = await response.json();
        setSongs(data.songs || []);
        setTotalPages(data.total_pages || 1);
      }
    } catch (error) {
      console.error('Failed to fetch songs:', error);
    } finally {
      setLoading(false);
    }
  }, [page, sortBy, searchQuery, selectedGenre, priceRange, bpmRange]);
  
  // Fetch featured songs
  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await fetch(`${API_URL}/api/songs/featured?limit=6`);
        if (response.ok) {
          const data = await response.json();
          setFeaturedSongs(data.songs || []);
        }
      } catch (error) {
        console.error('Failed to fetch featured:', error);
      }
    };
    fetchFeatured();
  }, []);
  
  useEffect(() => {
    fetchSongs();
  }, [fetchSongs]);
  
  // Handle play
  const handlePlay = (song) => {
    if (currentSong?.id === song.id) {
      setIsPlaying(!isPlaying);
    } else {
      setCurrentSong(song);
      setIsPlaying(true);
    }
  };
  
  // Handle buy
  const handleBuy = (song) => {
    if (!user) {
      toast.error('Please log in to purchase songs');
      navigate('/login');
      return;
    }
    navigate(`/songs/${song.id}`);
  };
  
  return (
    <div className="min-h-screen bg-zinc-950 text-white pb-24">
      {/* Header */}
      <div className="bg-gradient-to-b from-violet-900/30 to-transparent">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Headphones className="w-8 h-8 text-violet-400" />
                Song Marketplace
              </h1>
              <p className="text-zinc-400 mt-1">Discover and purchase beats, tracks, and songs</p>
            </div>
            <div className="flex items-center gap-3">
              {user && (
                <>
                  <Button
                    onClick={() => navigate('/songs/upload')}
                    className="bg-violet-600 hover:bg-violet-500"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Sell Your Music
                  </Button>
                  <NotificationCenter />
                </>
              )}
            </div>
          </div>
          
          {/* Marketplace Type Toggle */}
          <div className="flex gap-1 bg-zinc-800/50 rounded-lg p-1 mb-6 w-fit">
            <button
              onClick={() => navigate('/marketplace')}
              className="px-4 py-2 rounded-md text-sm text-zinc-400 hover:text-white transition-colors"
              data-testid="presets-tab-btn"
            >
              <Sliders className="w-4 h-4 inline mr-1.5" />
              Presets
            </button>
            <button
              className="px-4 py-2 rounded-md text-sm bg-violet-600 text-white"
            >
              <Headphones className="w-4 h-4 inline mr-1.5" />
              Songs
            </button>
          </div>
          
          {/* Search Bar */}
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchSongs()}
                placeholder="Search songs, artists, genres..."
                className="pl-12 pr-4 py-3 bg-zinc-900/50 border-zinc-800 text-lg"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="border-zinc-700"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </div>
          
          {/* Filters Panel */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 mt-4">
                  <div className="grid md:grid-cols-4 gap-6">
                    {/* Genre */}
                    <div>
                      <label className="text-sm text-zinc-400 mb-2 block">Genre</label>
                      <select
                        value={selectedGenre}
                        onChange={(e) => setSelectedGenre(e.target.value)}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                      >
                        {GENRES.map(genre => (
                          <option key={genre} value={genre}>{genre}</option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Price Range */}
                    <div>
                      <label className="text-sm text-zinc-400 mb-2 block">
                        Price: ${priceRange[0]} - ${priceRange[1]}
                      </label>
                      <Slider
                        value={priceRange}
                        min={0}
                        max={100}
                        step={5}
                        onValueChange={setPriceRange}
                      />
                    </div>
                    
                    {/* BPM Range */}
                    <div>
                      <label className="text-sm text-zinc-400 mb-2 block">
                        BPM: {bpmRange[0]} - {bpmRange[1]}
                      </label>
                      <Slider
                        value={bpmRange}
                        min={60}
                        max={180}
                        step={5}
                        onValueChange={setBpmRange}
                      />
                    </div>
                    
                    {/* Sort */}
                    <div>
                      <label className="text-sm text-zinc-400 mb-2 block">Sort By</label>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                      >
                        <option value="newest">Newest</option>
                        <option value="popular">Most Popular</option>
                        <option value="price_low">Price: Low to High</option>
                        <option value="price_high">Price: High to Low</option>
                        <option value="rating">Highest Rated</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="flex justify-end mt-4">
                    <Button onClick={fetchSongs} className="bg-violet-600 hover:bg-violet-500">
                      Apply Filters
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      
      {/* Featured Songs */}
      {featuredSongs.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center gap-2 mb-6">
            <Sparkles className="w-5 h-5 text-amber-400" />
            <h2 className="text-xl font-semibold">Featured Tracks</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {featuredSongs.map(song => (
              <SongCard
                key={song.id}
                song={song}
                onPlay={handlePlay}
                onBuy={handleBuy}
                isPlaying={isPlaying}
                currentSongId={currentSong?.id}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* All Songs */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">All Songs</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-zinc-800 text-white' : 'text-zinc-500'}`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-zinc-800 text-white' : 'text-zinc-500'}`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {[...Array(10)].map((_, i) => (
              <div key={`skeleton-${i}`} className="aspect-square bg-zinc-900/50 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : songs.length === 0 ? (
          <div className="text-center py-16">
            <Music className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl text-zinc-400 mb-2">No songs found</h3>
            <p className="text-zinc-500">Try adjusting your filters or search query</p>
          </div>
        ) : (
          <>
            <div className={viewMode === 'grid' 
              ? 'grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4' 
              : 'space-y-3'
            }>
              {songs.map(song => (
                <SongCard
                  key={song.id}
                  song={song}
                  onPlay={handlePlay}
                  onBuy={handleBuy}
                  isPlaying={isPlaying}
                  currentSongId={currentSong?.id}
                />
              ))}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-8">
                <Button
                  variant="outline"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="border-zinc-700"
                >
                  Previous
                </Button>
                <span className="flex items-center px-4 text-zinc-400">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="border-zinc-700"
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Audio Player */}
      <AnimatePresence>
        {currentSong && (
          <AudioPlayer
            song={currentSong}
            isPlaying={isPlaying}
            onTogglePlay={() => setIsPlaying(!isPlaying)}
            onClose={() => {
              setCurrentSong(null);
              setIsPlaying(false);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
